import React, { memo } from 'react';
import {
  BooleanInput,
  Datagrid,
  Filter,
  FunctionField,
  List,
  SelectInput,
  TextField,
  TextInput,
} from 'react-admin';
import {
  AchievementType,
  TriggerType,
  typeChoices,
  triggerTypeChoices,
  isProtectedAchievementName,
} from './achievementConstants';
import AchievementEdit from './AchievementEdit';
import InlineSvgPreview from './components/InlineSvgPreview';
import ProtectedAchievementNotice from './components/ProtectedAchievementNotice';

const INDICATOR = {
  true: '✅',
  false: '—',
};

const AchievementFilter = (props) => (
  <Filter {...props}>
    <TextInput label="Search name" source="q" alwaysOn resettable />
    <SelectInput label="Type" source="type" choices={typeChoices} allowEmpty />
    <SelectInput label="Trigger" source="triggerType" choices={triggerTypeChoices} allowEmpty />
    <BooleanInput label="Has Video" source="hasVideo" />
    <BooleanInput label="Has Icon" source="hasIcon" />
  </Filter>
);

const renderProgram = (record) => {
  if (!record) return '—';

  if (record.program) {
    return record.program.name
      ? `${record.program.name} (#${record.program.id})`
      : `Program #${record.program.id}`;
  }

  if (record.programId) {
    return `Program #${record.programId}`;
  }

  return '—';
};

const renderSession = (record) => {
  if (!record?.session) {
    return '—';
  }

  let programLabel = '';
  if (record.session.programName) {
    programLabel = ` • ${record.session.programName}`;
  } else if (record.session.programId) {
    programLabel = ` • Program #${record.session.programId}`;
  }
  return record.session.name
    ? `${record.session.name}${programLabel} (#${record.session.id})`
    : `Session #${record.session.id}`;
};

const renderIndicator = (value) => INDICATOR[String(Boolean(value))];

const AchievementExpandPanel = (props) => {
  const { record } = props;

  if (!record) {
    return null;
  }

  if (isProtectedAchievementName(record.name)) {
    return <ProtectedAchievementNotice record={record} />;
  }

  return <AchievementEdit {...props} />;
};

const AchievementList = (props) => {
  const handleRowClick = (id, basePath, record) =>
    isProtectedAchievementName(record?.name) ? 'none' : 'expand';

  return (
    <List
      {...props}
      perPage={100}
      exporter={false}
      bulkActionButtons={false}
      sort={{ field: 'type', order: 'ASC' }}
      filters={<AchievementFilter />}
      filterDefaultValues={{}}
    >
      <Datagrid expand={<AchievementExpandPanel />} rowClick={handleRowClick}>
        <TextField source="name" />
        <TextField source="description" />
        <FunctionField label="Program" render={renderProgram} />
        <FunctionField label="Session" render={renderSession} />
        <FunctionField source="type" render={(record) => AchievementType[record.type]} />
        <FunctionField source="triggerType" render={(record) => TriggerType[record.triggerType]} />
        <FunctionField
          label="Video"
          render={(record) => renderIndicator(record.video || record.videoId)}
        />
        <FunctionField label="Icon" render={(record) => renderIndicator(record.icon)} />
        <FunctionField
          label="Icon Preview"
          render={(record) => <InlineSvgPreview compact value={record.icon} />}
        />
        <FunctionField
          source="timesValue"
          label="Times Value"
          render={(record) => (record.timesValue != null ? record.timesValue : '—')}
        />
      </Datagrid>
    </List>
  );
};

export default memo(AchievementList);
